import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { GigyaService } from "./gigya.service";
import { Version, VersionService } from "./version.service";

@Injectable({
  providedIn: "root",
})
export class OrganizationPickerService {
  private static readonly STORAGE_KEY = "pickedOrganization";
  private static readonly TTL_MS = 60 * 1000;

  constructor(
    private gigyaService: GigyaService,
    private versionService: VersionService,
    private router: Router,
  ) { }

  /**
   * Called right after login succeeds. Resolves the organization for the
   * user and moves them straight to /continue whenever no choice is
   * actually required, so /picker only ever mounts when it has to show UI
   * (avoids the picker flashing before an automatic redirect).
   */
  public async proceed(): Promise<void> {
    console.info("OrganizationPickerService.proceed - version:", this.versionService.current);
    if (this.versionService.current === Version.B2C || this.versionService.current === Version.NSGO) {
      console.info("OrganizationPickerService.proceed - B2C/NSGO version, skipping org resolution, redirecting to /continue.");
      this.gigyaService.redirectToContinue();
      return;
    }

    const organizations = await this.getOrganizations();
    console.info("OrganizationPickerService.proceed - resolved organizations count:", organizations.length);

    if (organizations.length === 1) {
      console.info("OrganizationPickerService.proceed - exactly one organization, auto-selecting it.");
      await this.useOrganization(organizations[0]);
      return;
    }

    const hinted = this.findLoginHint(organizations);
    if (hinted) {
      console.info("OrganizationPickerService.proceed - login_hint matched an organization, using it.");
      await this.useOrganization(hinted);
      return;
    }

    const recent = this.findRecentPick(organizations);
    if (recent) {
      console.info("OrganizationPickerService.proceed - recent localStorage pick matched an organization, using it.");
      await this.useOrganization(recent);
      return;
    }

    if (organizations.length === 0) {
      console.warn("OrganizationPickerService.proceed - zero organizations resolved for this account - navigating to /picker which will show an empty/no-organization state.");
    } else {
      console.info("OrganizationPickerService.proceed - multiple organizations, no hint/recent match - navigating to /picker for manual selection. count:", organizations.length);
    }
    this.router.navigate(["/picker"], { queryParamsHandling: "preserve" });
  }

  public async useOrganization(org: any): Promise<void> {
    console.info("OrganizationPickerService.useOrganization - selecting organization bpid:", org?.bpid);
    this.storeRecentPick(org);
    const res = await this.gigyaService.execute(this.gigyaService.client.accounts.b2b.setOrganizationContext, {
      bpid: org.bpid,
    });
    if (res.errorCode !== 0) {
      console.warn("OrganizationPickerService.useOrganization - setOrganizationContext FAILED but proceeding to redirect anyway. errorCode:", res.errorCode, "bpid:", org?.bpid, "- this can leave the session without proper org context and may surface downstream as the OIDC fallback triggering on /continue.");
    } else {
      console.info("OrganizationPickerService.useOrganization - setOrganizationContext succeeded, redirecting to /continue.");
    }
    this.gigyaService.redirectToContinue();
  }

  private async getOrganizations(): Promise<any[]> {
    if (!this.gigyaService.data) {
      console.info("OrganizationPickerService.getOrganizations - no cached gigya data, calling accounts.getAccountInfo.");
      const res = await this.gigyaService.execute(this.gigyaService.client.accounts.getAccountInfo, {
        include: "groups,rba",
      });
      if (res.errorCode !== 0) {
        console.warn("OrganizationPickerService.getOrganizations - getAccountInfo FAILED, treating as zero organizations. errorCode:", res.errorCode, "- this forces the user down the /picker empty-state path.");
        return [];
      }
      this.gigyaService.setData(res);
    } else {
      console.info("OrganizationPickerService.getOrganizations - using cached gigya data.");
    }
    return (this.gigyaService.data as any)?.groups?.organizations ?? [];
  }

  private findLoginHint(organizations: any[]): any | null {
    const searchParams = new URLSearchParams(window.location.search);
    const orgId = searchParams.get("login_hint");
    if (!orgId) {
      return null;
    }
    const match = organizations.find((o) => o.id === orgId || o.bpid === orgId) ?? null;
    if (!match) {
      console.warn("OrganizationPickerService.findLoginHint - login_hint param present but did not match any organization. login_hint:", orgId);
    }
    return match;
  }

  private findRecentPick(organizations: any[]): any | null {
    try {
      const raw = window.localStorage.getItem(OrganizationPickerService.STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const data = JSON.parse(raw);
      if (!data.expiresAt || Date.now() > data.expiresAt) {
        console.info("OrganizationPickerService.findRecentPick - stored pick expired or malformed, clearing it.");
        window.localStorage.removeItem(OrganizationPickerService.STORAGE_KEY);
        return null;
      }
      const match = organizations.find((o) => o.bpid === data.bpid) ?? null;
      if (!match) {
        console.warn("OrganizationPickerService.findRecentPick - stored bpid did not match any current organization. bpid:", data.bpid);
      }
      return match;
    } catch (err) {
      console.warn("OrganizationPickerService.findRecentPick - localStorage read/parse failed (private browsing / disabled storage is common on mobile Safari) - treating as no recent pick.", err);
      try {
        window.localStorage.removeItem(OrganizationPickerService.STORAGE_KEY);
      } catch {
        console.warn("OrganizationPickerService.findRecentPick - localStorage unavailable, could not clean up stale key either.");
      }
      return null;
    }
  }

  private storeRecentPick(org: any): void {
    try {
      const data = { bpid: org.bpid, expiresAt: Date.now() + OrganizationPickerService.TTL_MS };
      window.localStorage.setItem(OrganizationPickerService.STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.warn("OrganizationPickerService.storeRecentPick - localStorage unavailable (e.g. disabled/private browsing) - skipping persistence of recent org pick.", err);
    }
  }
}
